import { useCallback, useEffect, useRef, useState } from "react";
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAlbum, type Album } from "../lib/api";
import { clearToken, readToken } from "../lib/token";
import type { Pending } from "../lib/store";
import { innerAspect } from "../lib/frames";
import { cameraSub, formatCountdownCoarse, uploadWindow } from "../lib/timewindow";
import { fmtDateShort } from "../lib/format";
import { pickFiles, useUploader } from "../lib/useUploader";
import { ROUND_OUTLINE, Viewfinder, useCamera } from "../components/Camera";
import { PhotoRoll } from "../components/PhotoRoll";
import { InAppBanner } from "../components/InAppBanner";
import { LangToggle } from "../components/LangToggle";
import { QrCard } from "../components/QrCard";
import { Sheet } from "../components/Sheet";
import { BackIcon, EventShell, QrIcon } from "../components/EventPage";
import { msgText, useT, type Key } from "../lib/i18n";

export const Route = createFileRoute("/a_/$slug/camera")({
  // Belum join: ke halaman acara dengan sheet nama terbuka.
  beforeLoad: ({ params }) => {
    if (!readToken(params.slug)) throw redirect({ to: "/a/$slug", params, search: { join: "camera" } });
  },
  component: CameraPage,
});

function CameraPage() {
  const { t } = useT();
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const album = useQuery({ queryKey: ["album", slug], queryFn: () => getAlbum(slug), retry: false });
  const [token] = useState(() => readToken(slug));

  const logout = useCallback(() => {
    clearToken(slug);
    void navigate({ to: "/a/$slug", params: { slug }, search: { join: "camera" } });
  }, [slug, navigate]);

  if (album.isPending) return <EventShell>{t("common.loading")}</EventShell>;
  if (album.error || !token) return <EventShell>{t("guest.load_failed")}</EventShell>;
  return <Guest album={album.data} token={token} onUnauthorized={logout} />;
}

// ---------- layar kamera ----------

function Guest({ album, token, onUnauthorized }: { album: Album; token: string; onUnauthorized: () => void }) {
  const { t } = useT();
  const slug = album.slug;
  const up = useUploader(album, token, onUnauthorized);
  const { status, photos, pending, send, busy, error, showBanner, setShowBanner, saved, now, closed, lockKey, allowed, left } = up;
  const [sheet, setSheet] = useState<"photos" | "qr" | null>(null);
  const cam = useCamera(up.capture);

  // Ide foto bergilir: tiap kali "?" membuka gelembung, tampil ide berikutnya.
  const [idea, setIdea] = useState(1);
  const [ideaOpen, setIdeaOpen] = useState(true);
  const toggleIdea = () => {
    if (!ideaOpen) setIdea((i) => (i % IDEAS) + 1);
    setIdeaOpen(!ideaOpen);
  };

  const [toast, setToast] = useState(false);
  useEffect(() => {
    if (!saved) return;
    setToast(true);
    const timer = setTimeout(() => setToast(false), 1500);
    return () => clearTimeout(timer);
  }, [saved]);

  const unsent = pending.filter((p) => p.status !== "uploading");
  const items = photos.data?.pages.flatMap((p) => p.items) ?? [];
  const last = pending.reduce<Pending | null>((a, p) => (!a || p.taken_at > a.taken_at ? p : a), null);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!last) return setLastUrl(null);
    const url = URL.createObjectURL(last.thumb);
    setLastUrl(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- satu URL per foto
  }, [last?.client_photo_id]);
  const thumb = lastUrl ?? [...items].sort((a, b) => b.id - a.id)[0]?.thumb_url ?? null;

  const lockText =
    status.data && lockKey
      ? t(lockKey, {
          count: status.data.device_photo_quota,
          date: fmtDateShort(uploadWindow(status.data)[0]),
          countdown: formatCountdownCoarse(uploadWindow(status.data)[0] - now),
        })
      : null;

  return (
    <div className="h-dvh overflow-hidden bg-ink text-cream">
      <main className="mx-auto flex h-full w-full max-w-md flex-col pt-[max(0.75rem,env(safe-area-inset-top))]">
        <InAppBanner show={!cam.active} />

        <header className="flex items-center gap-3 px-4 pb-3">
          <Link to="/a/$slug" params={{ slug }} aria-label={t("common.back")} className={ROUND_OUTLINE}>
            <BackIcon />
          </Link>
          <div className="min-w-0 flex-1 text-center">
            <h1 className="display truncate text-2xl">{album.title ?? "Qreate Moment"}</h1>
            <p className="text-xs text-sand">{status.data ? cameraSub(status.data, now) : " "}</p>
          </div>
          <button type="button" onClick={() => setSheet("qr")} aria-label={t("guest.camera.qr")} className={ROUND_OUTLINE}>
            <QrIcon />
          </button>
        </header>

        <Viewfinder cam={cam} filter={album.filter_preset} aspect={innerAspect(album.frame_id)} lock={lockText}>
          <button
            type="button"
            onClick={toggleIdea}
            aria-label={t("guest.camera.ideas")}
            aria-expanded={ideaOpen}
            aria-controls="photo-idea"
            className={`${ROUND_OUTLINE} absolute right-4 top-4`}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M8.5 8.5a3.5 3.5 0 0 1 6.9.9c0 2.4-3.4 3.1-3.4 5.1M12 18.5h.01" />
            </svg>
          </button>
          <p
            id="photo-idea"
            role="status"
            hidden={!ideaOpen}
            className="absolute right-4 top-[4.25rem] max-w-[calc(100%-2rem)] rounded-xl bg-cream px-4 py-2 text-[13px] text-ink shadow-lg after:absolute after:-top-1.5 after:right-4 after:h-3 after:w-3 after:rotate-45 after:rounded-[2px] after:bg-cream"
          >
            {t("guest.camera.ideas")}: {t(`guest.camera.idea_${idea}` as Key)}
          </p>
          {toast && (
            <div
              role="status"
              className="absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/15 bg-black/70 px-4 py-2 text-sm backdrop-blur"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="m5 12 5 5 9-10" />
              </svg>
              {t("guest.camera.saved")}
            </div>
          )}
        </Viewfinder>

        <div className="grid grid-cols-[1fr_auto_1fr] items-center px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
          <button
            type="button"
            onClick={() => setSheet("photos")}
            aria-label={t("guest.your_photos")}
            className="relative h-16 w-16 justify-self-start"
          >
            {thumb ? (
              <img
                key={saved}
                src={thumb}
                alt=""
                className={`h-full w-full rounded-full object-cover ${saved ? "animate-toss" : ""}`}
              />
            ) : (
              <span className="block h-full w-full rounded-full border-[1.5px] border-dashed border-cream/40" />
            )}
            {unsent.length > 0 && <span className="absolute right-0 top-0 h-3 w-3 rounded-full bg-amber-400" />}
          </button>
          <button
            type="button"
            disabled={!allowed || !cam.active}
            onClick={cam.shoot}
            aria-label={t("guest.camera.shoot")}
            className="group h-20 w-20 rounded-full border-2 border-cream p-[5px] disabled:opacity-40"
          >
            <span className="block h-full w-full rounded-full bg-cream transition group-active:scale-[0.88]" />
          </button>
          <ShotCounter left={left} total={status.data?.device_photo_quota ?? 0} />
        </div>
      </main>

      {sheet === "photos" && (
        <Sheet title={t("guest.your_photos")} onClose={() => setSheet(null)}>
          <div className="space-y-4">
            {showBanner && unsent.length > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-100 px-4 py-3 text-sm text-amber-900">
                <span>
                  {closed
                    ? t("guest.unsent_closed", { count: unsent.length })
                    : t("guest.unsent", { count: unsent.length })}
                </span>
                {!closed && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowBanner(false);
                      // Berurutan: rate limit request 10/menit/device, paralel akan langsung kena 429.
                      void unsent.reduce((chain, p) => chain.then(() => send(p)), Promise.resolve());
                    }}
                    className="rounded-full bg-amber-900 px-3 py-1 text-white"
                  >
                    {t("guest.resend_all")}
                  </button>
                )}
              </div>
            )}
            {busy > 0 && <p className="text-sm text-neutral-400">{t("guest.processing")}</p>}
            {error && <p className="text-sm text-red-400">{msgText(t, error)}</p>}
            <PhotoRoll
              pending={pending}
              items={items}
              onRetry={(p) => void send(p)}
              hasMore={photos.hasNextPage}
              loadingMore={photos.isFetchingNextPage}
              onMore={() => void photos.fetchNextPage()}
            />
            <label
              className={`flex w-full cursor-pointer items-center justify-center rounded-full border border-white/20 px-4 py-3 text-sm font-medium ${allowed ? "" : "pointer-events-none opacity-40"}`}
            >
              {t("guest.camera.upload")}
              <input type="file" accept="image/*" multiple disabled={!allowed} onChange={pickFiles(up.capture)} className="sr-only" />
            </label>
            <div className="flex justify-center pt-2">
              <LangToggle className="rounded-full bg-cream px-3 py-1" />
            </div>
          </div>
        </Sheet>
      )}

      {sheet === "qr" && (
        <Sheet title={t("guest.camera.qr_title")} onClose={() => setSheet(null)}>
          <QrCard url={`${location.origin}/a/${slug}`} filename={`qr-${slug}`} />
        </Sheet>
      )}
    </div>
  );
}

/** Jumlah kunci `guest.camera.idea_N` di en.json & id.json. */
const IDEAS = 6;

/** Penghitung "SHOTS terpakai / kuota"; angka terpakai bergulir saat bertambah. */
function ShotCounter({ left, total }: { left: number | null; total: number }) {
  const { t } = useT();
  // Tidak bergulir untuk angka pertama yang tampil, hanya saat berubah.
  const first = useRef(left);
  if (first.current === null) first.current = left;
  return (
    <div
      role="img"
      aria-label={left === null ? t("guest.camera.shots") : t("guest.camera.left", { count: left })}
      className="flex h-[72px] w-[72px] flex-col items-center justify-center justify-self-end overflow-hidden rounded-full border-[1.5px] border-cream text-center"
    >
      <span className="text-[9px] font-semibold uppercase tracking-[0.08em]">{t("guest.camera.shots")}</span>
      {left !== null && (
        <span className="font-display text-[32px] leading-[0.85] tabular-nums">
          <span key={left} className={`inline-block ${left === first.current ? "" : "animate-roll"}`}>{total - left}</span>
          <span className="text-[15px]"> / {total}</span>
        </span>
      )}
    </div>
  );
}
